// src/hooks/useBenderTracking.js
import { useState, useEffect, useRef } from 'react';
import { formatDuration, getDistanceFromLatLonInM } from '../utils/scoring';
import { ensureNotifPermission, scheduleBenderCheckins, cancelBenderCheckins } from '../native/notify';

const INITIAL_STATS = {
    drinks: 0, 
    waters: 0, 
    hydration: 100, 
    startTime: null, 
    duration: "00:00:00",
    drinkOfChoice: "CHOOSE DRINK", // 1.4.3 copy soften (was "PICK POISON")
    path: [], 
    location: null, 
    distance: 0, 
    steps: 0,
    lastActiveTime: null 
};

// If no GPS or interaction for 12 hours, the bender is officially over.
const AUTO_END_HOURS = 12; 

const useBenderTracking = (email, onNotify) => {
    // True only while the native hardware pedometer stream is actually running.
    // iOS: CMPedometer. Android: TYPE_STEP_COUNTER (bender-pedometer android/).
    // When it is NOT running (web, devices without the sensor, permission
    // denied), the GPS-distance step estimate below stays the owner — this ref
    // is what fixes "zero steps on Android" from before the Android plugin
    // existed: the estimate was disabled for ALL native platforms while the
    // pedometer only existed on iOS.
    const pedometerActiveRef = useRef(false);
    // Timestamp of the last ACCEPTED path point. The speed filter must measure
    // distance-since-last-point over time-since-last-POINT; using the time of
    // the last raw fix (which Android's continuous GPS delivers constantly)
    // made real walking segments look like teleports and dropped them.
    const lastAcceptedAtRef = useRef(null);
    // --- 1. LOAD FROM LOCAL STORAGE ON INIT ---
    const [stats, setStats] = useState(() => {
        if (typeof window === 'undefined') return INITIAL_STATS;
        try {
            const savedPath = localStorage.getItem('bender_path');
            const savedStats = localStorage.getItem('bender_stats'); 
            
            const parsedStats = savedStats ? JSON.parse(savedStats) : {};
            const parsedPath = savedPath ? JSON.parse(savedPath) : [];

            return {
                ...INITIAL_STATS,
                ...parsedStats, 
                path: parsedPath
            };
        } catch (e) {
            console.warn("Failed to load session", e);
            return INITIAL_STATS;
        }
    });

    // --- 2. AUTO-RESUME TRACKING ---
    const [isTracking, setIsTracking] = useState(() => {
        return !!stats.startTime; 
    });

    // --- 3. SAVE SIDE EFFECTS ---
    useEffect(() => {
        if (stats.path && stats.path.length > 0) {
            localStorage.setItem('bender_path', JSON.stringify(stats.path));
        }

        if (stats.startTime) {
            const sessionData = {
                distance: stats.distance,
                steps: stats.steps,
                startTime: stats.startTime,
                hydration: stats.hydration,
                lastActiveTime: stats.lastActiveTime,
                drinks: stats.drinks,
                waters: stats.waters,
                drinkOfChoice: stats.drinkOfChoice
            };
            localStorage.setItem('bender_stats', JSON.stringify(sessionData));
        }
    }, [stats]);

    // --- 4. START NIGHT ACTION ---
    const startNight = () => {
        setIsTracking(true);
        localStorage.removeItem('bender_path'); 
        localStorage.removeItem('bender_stats');

        setStats(prev => ({ 
            ...prev, 
            startTime: Date.now(), 
            lastActiveTime: Date.now(),
            path: prev.location ? [prev.location] : [], 
            distance: 0, 
            steps: 0, 
            hydration: 100,
            drinks: 0,
            waters: 0
        }));
        if(onNotify) onNotify('info', "Tracking Started. Let's go! 🚀");
    };

    // --- 4b. END NIGHT ACTION ---
    // Fully clears the session so an ended bender can't resurrect on reload.
    // (isTracking is derived from stats.startTime on init, so startTime must be
    //  wiped here AND the persisted keys removed.)
    const endNight = () => {
        setIsTracking(false);
        localStorage.removeItem('bender_path');
        localStorage.removeItem('bender_stats');
        setStats(prev => ({ ...INITIAL_STATS, location: prev.location }));
    };

    // --- 5. TIMER, HYDRATION DECAY & AUTO-TIMEOUT ---
    useEffect(() => {
        if (!isTracking || !stats.startTime) return;
        
        const timerInterval = setInterval(() => {
          const now = Date.now();
          const lastActive = stats.lastActiveTime || stats.startTime;

          // 🛑 AUTO-END CHECK: Has it been 12 hours?
          if (now - lastActive > (AUTO_END_HOURS * 60 * 60 * 1000)) {
              setIsTracking(false);
              localStorage.removeItem('bender_path');
              localStorage.removeItem('bender_stats');
              if (onNotify) onNotify('info', "Session auto-ended due to 12h of inactivity.");
              return;
          }

          setStats(s => ({
            ...s,
            duration: formatDuration(s.startTime),
            hydration: Math.max(0, s.hydration - 0.03)
          }));
        }, 1000);
    
        const nudgeInterval = setInterval(() => {
            if (stats.hydration <= 0 && onNotify) {
                 onNotify('water', "DRINK SOME WATER! 🚰");
            }
            else if (stats.hydration < 20 && stats.hydration > 19 && onNotify) {
                 onNotify('water', "Hydration Critical! 💧");
            }
        }, 5000);
    
        return () => { clearInterval(timerInterval); clearInterval(nudgeInterval); };
    }, [isTracking, stats.startTime, stats.lastActiveTime, stats.hydration, onNotify]);

    // --- 6. GPS LOGIC (SMART CATCH-UP) ---
    useEffect(() => {
        if (!email) return;

        // On native, the CMPedometer effect below owns the step count, so GPS
        // must not overwrite it with its distance-based estimate.
        const isNativeApp = typeof window !== 'undefined' && !!window.Capacitor?.isNativePlatform?.();

        // Shared position handler — used by BOTH the real geolocation watch and
        // the dev override below, so a spoofed position runs through the exact
        // same accuracy / jitter / speed filters as a real one.
        const handlePosition = (latitude, longitude, accuracy) => {
            // 🛑 Accuracy Filter. If it's bouncing around worse than 50 meters, completely ignore it.
            if (accuracy > 50) return;

            const newLoc = [latitude, longitude];
            const now = Date.now();

            setStats(prev => {
              let newState = { ...prev, location: newLoc };

              if (isTracking) {
                 const lastPt = prev.path.length > 0 ? prev.path[prev.path.length - 1] : null;
                 // Speed must be measured against the last ACCEPTED point, not the
                 // last raw fix — rejected jitter fixes used to reset the clock and
                 // make the next real segment look impossibly fast.
                 const lastTime = lastAcceptedAtRef.current || prev.lastActiveTime || now;
                 const timeDelta = (now - lastTime) / 1000;

                 const distMeters = lastPt
                    ? getDistanceFromLatLonInM(lastPt[0], lastPt[1], newLoc[0], newLoc[1])
                    : 0;
                 const speed = timeDelta > 0 ? (distMeters / timeDelta) : 0;

                 const isTooFast = speed > 4.5; // ~10 mph (prevents driving from counting as steps)
                 // 🛑 Couch Filter. Kills desk/GPS drift under 10m.
                 const isJitter = distMeters < 10;
                 const isGlitch = speed > 50; // Teleportation filter

                 // We only draw the line and add steps if it's a legit human walking
                 if (lastPt && !isTooFast && !isJitter && !isGlitch) {
                     newState.path = [...prev.path, newLoc];
                     newState.distance = (prev.distance || 0) + distMeters;
                     // Distance-based step estimate — owns the count everywhere the
                     // hardware pedometer stream isn't live (web, no sensor, denied).
                     if (!isNativeApp || !pedometerActiveRef.current) {
                         newState.steps = Math.floor(newState.distance / 0.762);
                     }
                     lastAcceptedAtRef.current = now;
                 } else if (!lastPt) {
                     // First point of the path
                     newState.path = [newLoc];
                     lastAcceptedAtRef.current = now;
                 }

                 newState.lastActiveTime = now;
              }
              return newState;
            });
        };

        // --- DEV GEO OVERRIDE (development builds only) ---------------------
        // Feeds a fake coordinate straight into handlePosition — no browser
        // permission prompt, fully deterministic. Two sources (query param wins):
        //     ?geo=<lat>,<lng>                 e.g.  ?geo=33.5641,-86.0665
        //     localStorage.bq_dev_geo="lat,lng"  (set from DevTools console)
        // The value is re-read every 4s, so updating bq_dev_geo live simulates
        // movement. To register as a WALKING step it must pass the same filters
        // as real GPS: move >=10m and <=~18m between reads (>10m & <4.5 m/s @4s).
        const readDevGeo = () => {
            try {
                const params = new URLSearchParams(window.location.search);
                const raw = params.get('geo') || window.localStorage.getItem('bq_dev_geo');
                if (!raw) return null;
                const [lat, lng] = String(raw).split(',').map(Number);
                if (Number.isFinite(lat) && Number.isFinite(lng)) return [lat, lng];
            } catch (e) { /* ignore malformed override */ }
            return null;
        };

        if (process.env.NODE_ENV === 'development' && readDevGeo()) {
            console.log('[bq-dev-geo] GPS override ACTIVE — real geolocation bypassed. Value:', readDevGeo());
            const first = readDevGeo();
            if (first) handlePosition(first[0], first[1], 10); // accuracy 10m passes the filter
            const devInterval = setInterval(() => {
                const loc = readDevGeo();
                if (loc) handlePosition(loc[0], loc[1], 10);
            }, 4000);
            return () => clearInterval(devInterval);
        }
        // --- END DEV GEO OVERRIDE ------------------------------------------

        // --- NATIVE BACKGROUND LOCATION -----------------------------------
        // On native, use @capacitor-community/background-geolocation as the
        // location source so the path keeps building and a rep keeps
        // broadcasting with the screen off / app backgrounded — impossible with
        // the web watchPosition below. Feeds the SAME handlePosition filters.
        if (isNativeApp) {
            let watcherId = null;
            let removed = false;

            (async () => {
                try {
                    const { default: BackgroundGeolocation } = await import('../native/backgroundGeolocation');
                    const id = await BackgroundGeolocation.addWatcher(
                        {
                            backgroundMessage: 'Tracking your night out. Tap to return.',
                            backgroundTitle: 'Bender Quest is live',
                            requestPermissions: true,
                            stale: false,
                            distanceFilter: 8, // metres; handlePosition still applies the 10m jitter filter
                        },
                        (location, error) => {
                            if (error) {
                                console.warn('BackgroundGeolocation error:', error.code || error);
                                return;
                            }
                            if (location) {
                                handlePosition(
                                    location.latitude,
                                    location.longitude,
                                    location.accuracy != null ? location.accuracy : 10
                                );
                            }
                        }
                    );
                    // If the effect already cleaned up while addWatcher was in
                    // flight, remove the just-created watcher immediately.
                    if (removed) BackgroundGeolocation.removeWatcher({ id });
                    else watcherId = id;
                } catch (e) {
                    console.warn('BackgroundGeolocation unavailable:', e);
                }
            })();

            return () => {
                removed = true;
                if (watcherId) {
                    import('../native/backgroundGeolocation')
                        .then(({ default: BG }) => BG.removeWatcher({ id: watcherId }))
                        .catch(() => {});
                }
            };
        }
        // --- END NATIVE BACKGROUND LOCATION -------------------------------

        if (!navigator.geolocation) return;

        let wakeLock = null;
        const requestWakeLock = async () => {
          if ('wakeLock' in navigator && isTracking) {
            try { wakeLock = await navigator.wakeLock.request('screen'); }
            catch (err) { console.log("Wake Lock skipped:", err); }
          }
        };
        if (isTracking) requestWakeLock();

        navigator.geolocation.getCurrentPosition(
            (pos) => {
                const { latitude, longitude } = pos.coords;
                setStats(prev => ({ ...prev, location: [latitude, longitude] }));
            },
            (err) => console.warn("Initial GPS failed", err),
            { enableHighAccuracy: true }
        );

        const watchId = navigator.geolocation.watchPosition(
          (pos) => {
            const { latitude, longitude, accuracy } = pos.coords;
            handlePosition(latitude, longitude, accuracy);
          },
          (err) => console.warn("GPS Error", err),
          {
            enableHighAccuracy: true,
            maximumAge: 10000, // Allows slightly cached locations to smooth out bounces
            timeout: 10000
          }
        );
        
        return () => {
            navigator.geolocation.clearWatch(watchId);
            if (wakeLock) wakeLock.release();
        };
    }, [email, isTracking]);

    // --- 7. NATIVE PEDOMETER (real hardware step count) ------------------
    // On native builds, replace the GPS-derived step estimate with CMPedometer
    // (the same hardware counter Apple Health uses). Runs alongside GPS, which
    // still owns path + distance. No-op on web, where the estimate stays.
    useEffect(() => {
        const isNativeApp = typeof window !== 'undefined' && !!window.Capacitor?.isNativePlatform?.();
        if (!isNativeApp || !isTracking || !stats.startTime) return;

        let listener = null;
        let cancelled = false;

        (async () => {
            try {
                const { Pedometer } = await import('bender-pedometer');
                const { available } = await Pedometer.isAvailable();
                if (!available || cancelled) return;
                listener = await Pedometer.addListener('step', (e) => {
                    const steps = Number(e?.steps) || 0;
                    setStats(s => ({ ...s, steps }));
                });
                // Pass the session start so a resumed session backfills steps
                // taken while the app was suspended (CMPedometer keeps ~7 days;
                // Android's since-boot counter covers any gap on the same boot).
                await Pedometer.start({ startTime: stats.startTime });
                // Only now does the hardware own the count — start() may have
                // been refused (Android activity-recognition permission denied),
                // in which case the GPS estimate stays the owner.
                if (!cancelled) pedometerActiveRef.current = true;
            } catch (err) {
                console.warn('Pedometer unavailable, keeping GPS estimate:', err);
                pedometerActiveRef.current = false;
            }
        })();

        return () => {
            cancelled = true;
            pedometerActiveRef.current = false;
            (async () => {
                try {
                    const { Pedometer } = await import('bender-pedometer');
                    await Pedometer.stop();
                } catch (e) { /* ignore */ }
                if (listener) { try { listener.remove(); } catch (e) { /* ignore */ } }
            })();
        };
    }, [isTracking, stats.startTime]);

    // --- 8. NATIVE "STILL ON THE BENDER?" CHECK-INS ----------------------
    // Schedule staggered local notifications relative to the session start so
    // iOS nudges a forgotten/pocketed user even with the screen off. Cancelled
    // when the session ends. No-op on web.
    useEffect(() => {
        const isNativeApp = typeof window !== 'undefined' && !!window.Capacitor?.isNativePlatform?.();
        if (!isNativeApp) return;

        if (isTracking && stats.startTime) {
            (async () => {
                const granted = await ensureNotifPermission();
                if (granted) await scheduleBenderCheckins(stats.startTime);
            })();
        }

        return () => { cancelBenderCheckins(); };
    }, [isTracking, stats.startTime]);

    return { stats, setStats, startNight, endNight, isTracking, setIsTracking };
};

export default useBenderTracking;
